export class InvalidCompanyCodeError extends Error {
  constructor() {
    super('Invalid company code. Contact your admin.');
    this.name = 'InvalidCompanyCodeError';
  }
}

export class EmailAlreadyRegisteredError extends Error {
  constructor() {
    super('Email already registered.');
    this.name = 'EmailAlreadyRegisteredError';
  }
}

export class NetworkError extends Error {
  constructor() {
    super('Connection failed. Check your internet.');
    this.name = 'NetworkError';
  }
}

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const isNetworkError = (error: unknown): boolean => {
  if (error instanceof TypeError) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return message.includes('network') || message.includes('fetch') || message.includes('offline');
};

const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:3001';
};

const postJson = async <T>(path: string, body: Record<string, string>): Promise<T> => {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch (_) {
    payload = null;
  }

  if (!response.ok) {
    const fallbackMessage = `Request failed with status ${response.status}`;
    const apiMessage =
      payload && typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : fallbackMessage;

    throw new ApiError(apiMessage, response.status);
  }

  return payload as T;
};

export const startGoogleOAuth = async (params: {
  redirectTo?: string;
  companyCode: string;
  fullName: string;
  employeeId: string;
}): Promise<void> => {
  try {
    const data = await postJson<{ auth_url: string }>('/api/auth/google/url', {
      redirect_to: params.redirectTo || `${window.location.origin}/oauth/callback`,
      company_code: params.companyCode.trim().toUpperCase(),
      full_name: params.fullName.trim(),
      employee_id: params.employeeId.trim(),
    });

    if (!data.auth_url) {
      throw new Error('Google auth URL was not returned by server.');
    }

    window.location.assign(data.auth_url);
  } catch (error) {
    if (isNetworkError(error)) {
      throw new NetworkError();
    }

    throw error;
  }
};

export const finalizeGoogleOnboarding = async (params: {
  accessToken: string;
  companyCode?: string;
  fullName?: string;
  employeeId?: string;
  oauthState?: string;
}): Promise<{ ok: boolean }> => {
  try {
    return await postJson<{ ok: boolean }>('/api/auth/google/finalize', {
      access_token: params.accessToken,
      company_code: params.companyCode || '',
      full_name: params.fullName || '',
      employee_id: params.employeeId || '',
      oauth_state: params.oauthState || '',
    });
  } catch (error) {
    if (isNetworkError(error)) {
      throw new NetworkError();
    }
    throw error;
  }
};

export const validateCompanyCode = async (companyCode: string): Promise<string> => {
  const normalizedCode = companyCode.trim().toUpperCase();

  try {
    const data = await postJson<{ company_code: string }>('/api/auth/validate-company-code', {
      company_code: normalizedCode,
    });
    return data.company_code;
  } catch (error) {
    if (error instanceof ApiError && error.status === 400) {
      throw new InvalidCompanyCodeError();
    }

    if (error instanceof InvalidCompanyCodeError) {
      throw error;
    }

    if (isNetworkError(error)) {
      throw new NetworkError();
    }

    throw new InvalidCompanyCodeError();
  }
};

export const signUp = async (params: {
  email: string;
  password: string;
  fullName: string;
  companyCode: string;
  employeeId?: string;
}): Promise<{ needsEmailConfirmation: boolean }> => {
  const normalizedEmail = params.email.trim().toLowerCase();

  try {
    const data = await postJson<{ needs_email_confirmation?: boolean }>('/api/auth/signup', {
      email: normalizedEmail,
      password: params.password,
      full_name: params.fullName.trim(),
      company_code: params.companyCode.trim().toUpperCase(),
      employee_id: params.employeeId?.trim() || '',
    });

    return {
      needsEmailConfirmation: Boolean(data.needs_email_confirmation),
    };
  } catch (error) {
    if (error instanceof ApiError) {
      const message = error.message.toLowerCase();
      if (message.includes('already registered') || message.includes('already exists')) {
        throw new EmailAlreadyRegisteredError();
      }
    }

    if (isNetworkError(error)) {
      throw new NetworkError();
    }

    throw error;
  }
};

export const signIn = async (
  email: string,
  password: string,
): Promise<{
  access_token: string;
  refresh_token?: string;
  role?: string;
}> => {
  try {
    const data = await postJson<{
      access_token: string;
      refresh_token?: string;
      role?: string;
    }>('/api/auth/signin', {
      email: email.trim().toLowerCase(),
      password,
    });
    return data;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      throw new Error('Invalid email or password.');
    }

    if (isNetworkError(error)) {
      throw new NetworkError();
    }

    throw error;
  }
};

export const requestPasswordReset = async (email: string, redirectTo?: string): Promise<{ ok: boolean; message: string }> => {
  try {
    return await postJson<{ ok: boolean; message: string }>('/api/auth/forgot-password', {
      email: email.trim().toLowerCase(),
      redirect_to: redirectTo || `${window.location.origin}/reset-password`,
    });
  } catch (error) {
    if (isNetworkError(error)) {
      throw new NetworkError();
    }
    throw error;
  }
};

export const resetPassword = async (params: {
  newPassword: string;
  accessToken?: string;
  refreshToken?: string;
}): Promise<{ ok: boolean }> => {
  try {
    return await postJson<{ ok: boolean }>('/api/auth/reset-password', {
      new_password: params.newPassword,
      access_token: params.accessToken || '',
      refresh_token: params.refreshToken || '',
    });
  } catch (error) {
    if (isNetworkError(error)) {
      throw new NetworkError();
    }
    throw error;
  }
};
